import React, { useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Copy } from 'lucide-react'
import { toast } from '@/components/ui/use-toast'
import ContentRenderer from '../renderers/ContentRenderer'
import { ContentEditor } from '../renderers'
import { Content } from '../useContentData'
import { VersionAlert } from './alerts/VersionAlert'
import { ExitEditConfirmationDialog } from '@/components/ui/exit-edit-confirmation-dialog'
import { EnhanceMediaDropdown } from './EnhanceMediaDropdown'
import { PlatformPostType } from '@/types/content.types'

interface ContentPreviewProps {
    content: Content
    editMode: boolean
    versionNumber?: number
    isViewingOldVersion: boolean
    onSaveEdit: () => Promise<void>
    onCancelEdit: () => void
    onRestoreVersion: () => void
    onExitEditRequest?: (exitHandler: () => void) => void
    onEnhanceMedia?: (mediaType: 'image' | 'video') => void
    enhancingMedia?: boolean
}

/**
 * Component for rendering content preview with various display options
 */
export function ContentPreview({
    content,
    editMode,
    versionNumber,
    isViewingOldVersion,
    onSaveEdit,
    onCancelEdit,
    onRestoreVersion,
    onExitEditRequest,
    onEnhanceMedia,
    enhancingMedia
}: ContentPreviewProps) {
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
    const [showExitConfirmation, setShowExitConfirmation] = useState(false)
    const [isSaving, setIsSaving] = useState(false)

    // Extract platform_post_type from content_json for media filtering
    const platformPostType: PlatformPostType | undefined = content.content_json?.platform_post_type

    // Handle unsaved changes from editor
    const handleUnsavedChanges = (hasChanges: boolean) => {
        setHasUnsavedChanges(hasChanges)
    }

    // Handle exit edit request (called from toolbar)
    const handleExitEditRequest = () => {
        if (hasUnsavedChanges) {
            setShowExitConfirmation(true)
        } else {
            onCancelEdit()
        }
    }

    // Handle confirmation dialog actions
    const handleConfirmationAction = async (action: 'save' | 'discard') => {
        if (action === 'save') {
            setIsSaving(true)
            try {
                await onSaveEdit()
                setShowExitConfirmation(false)
            } catch (error) {
                console.error('Error saving:', error)
            } finally {
                setIsSaving(false)
            }
        } else {
            setShowExitConfirmation(false)
            onCancelEdit()
        }
    }

    // Expose the exit handler to parent
    React.useEffect(() => {
        if (onExitEditRequest) {
            onExitEditRequest(handleExitEditRequest)
        }
    }, [onExitEditRequest])

    // Check if we have bonus metadata for the bonus section
    const hasBonusData = useMemo(() => {
        return !!(content?.content_json?.bonus_metadata && typeof content.content_json.bonus_metadata === 'object')
    }, [content])

    return (
        <div className="flex flex-col flex-1 min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/20 relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            
            {isViewingOldVersion && (
                <VersionAlert onRestoreClick={onRestoreVersion} />
            )}
            
            {/* Enhanced Preview Section */}
            <div className="flex-1 px-6 pb-6 pt-6 relative">
                <div className="group relative overflow-hidden bg-gradient-to-br from-white via-indigo-50/30 to-purple-50/40 rounded-2xl border border-indigo-200/60 shadow-lg hover:shadow-2xl transition-all duration-500">
                    <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/30 via-transparent to-purple-50/20" />
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />
                    <div className="relative">
                    {editMode ? (
                        <div className="flex flex-col">
                            {/* Edit mode toolbar */}
                            {onEnhanceMedia && (
                                <div className="border-b p-4 bg-gray-50 dark:bg-gray-900 hidden">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-medium text-muted-foreground">
                                            Edit Tools
                                        </h3>
                                        <EnhanceMediaDropdown
                                            onEnhanceMedia={onEnhanceMedia}
                                            enhancingMedia={enhancingMedia}
                                            platformPostType={platformPostType}
                                        />
                                    </div>
                                </div>
                            )}
                            <ContentEditor 
                                content={content}
                                onSaved={onSaveEdit}
                                onCancel={handleExitEditRequest}
                                onUnsavedChanges={handleUnsavedChanges}
                            />
                        </div>
                    ) : (
                        <ContentRenderer 
                            content={content}
                            versionNumber={versionNumber}
                        />
                    )}
                    </div>
                </div>
            </div>
            
            {/* Enhanced Bonus Data Section */}
            {hasBonusData && (
                <div className="flex-1 px-6 pb-6 relative">
                    <div className="group relative overflow-hidden bg-gradient-to-br from-amber-50/80 via-orange-50/60 to-white rounded-2xl border border-amber-200/60 shadow-lg hover:shadow-2xl transition-all duration-500">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/30 via-transparent to-orange-50/20" />
                        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-200/20 to-transparent rounded-full -translate-y-16 translate-x-16" />
                        <div className="relative">
                            <div className="p-6 pb-4 border-b border-amber-100/50">
                                <h3 className="text-xl font-bold bg-gradient-to-r from-amber-800 to-orange-700 bg-clip-text text-transparent">
                                    Bonus Data
                                </h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Additional metadata to enhance your content
                                </p>
                            </div>
                            <div className="p-6">
                            {Object.entries(content.content_json.bonus_metadata as Record<string, any>).map(([key, value]) => (
                                <div key={key} className="mb-8">
                                    <h4 className="font-medium mb-2">
                                        {key.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                    </h4>
                                    {renderBonusDataValue(value)}
                                </div>
                            ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Exit Edit Confirmation Dialog */}
            <ExitEditConfirmationDialog
                isOpen={showExitConfirmation}
                onConfirm={handleConfirmationAction}
                onCancel={() => setShowExitConfirmation(false)}
                isSaving={isSaving}
            />
        </div>
    )
}

/**
 * Helper function to render different types of bonus data values
 */
function renderBonusDataValue(value: any) {
    // For arrays
    if (Array.isArray(value)) {
        return (
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {value.map((item, i) => (
                    <div key={i} className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/20 via-transparent to-orange-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative p-4">
                            <div className="flex justify-between items-start">
                                <div className="font-medium mt-1 text-gray-800">{item}</div>
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8 rounded-full hover:bg-amber-100/50 transition-all duration-300 transform hover:scale-110" 
                                    onClick={() => {
                                        navigator.clipboard.writeText(item);
                                        toast({
                                            title: "Copied!",
                                            description: "Text copied to clipboard",
                                        });
                                    }}
                                >
                                    <Copy className="h-4 w-4 text-amber-600" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        )
    }
    
    // For nested objects
    if (typeof value === 'object' && value !== null) {
        return (
            <div className="space-y-6">
                {Object.entries(value).map(([subKey, subValue]) => (
                    <div key={subKey} className="space-y-3">
                        <h5 className="font-medium">
                            {subKey.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </h5>
                        {renderNestedBonusValue(subValue)}
                    </div>
                ))}
            </div>
        )
    }
    
    // For primitives (strings, numbers, booleans)
    return (
        <div className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/20 via-transparent to-orange-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative p-4">
                <div className="flex justify-between items-start">
                    <p className="pr-8 text-gray-800">{String(value)}</p>
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 rounded-full hover:bg-amber-100/50 transition-all duration-300 transform hover:scale-110" 
                        onClick={() => {
                            navigator.clipboard.writeText(String(value));
                            toast({
                                title: "Copied!",
                                description: "Text copied to clipboard",
                            });
                        }}
                    >
                        <Copy className="h-4 w-4 text-amber-600" />
                    </Button>
                </div>
            </div>
        </div>
    )
}

/**
 * Helper function to render nested bonus data values
 */
function renderNestedBonusValue(value: any) {
    if (typeof value === 'string') {
        return (
            <div className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-50/20 via-transparent to-orange-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative p-4">
                    <div className="flex justify-between items-start">
                        <p className="pr-8 text-gray-800">{value}</p>
                        <Button 
                            variant="ghost" 
                            size="icon" 
                            className="h-8 w-8 rounded-full hover:bg-amber-100/50 transition-all duration-300 transform hover:scale-110" 
                            onClick={() => {
                                navigator.clipboard.writeText(value);
                                toast({
                                    title: "Copied!",
                                    description: "Text copied to clipboard",
                                });
                            }}
                        >
                            <Copy className="h-4 w-4 text-amber-600" />
                        </Button>
                    </div>
                </div>
            </div>
        )
    } 
    
    if (Array.isArray(value)) {
        return (
            <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {value.map((item, i) => (
                    <div key={i} className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300 hover:scale-105">
                        <div className="absolute inset-0 bg-gradient-to-br from-amber-50/20 via-transparent to-orange-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                        <div className="relative p-4">
                            <div className="flex justify-between items-start">
                                <div className="font-medium mt-1 text-gray-800">{item}</div>
                                <Button 
                                    variant="ghost" 
                                    size="icon" 
                                    className="h-8 w-8 rounded-full hover:bg-amber-100/50 transition-all duration-300 transform hover:scale-110" 
                                    onClick={() => {
                                        navigator.clipboard.writeText(item);
                                        toast({
                                            title: "Copied!",
                                            description: "Text copied to clipboard",
                                        });
                                    }}
                                >
                                    <Copy className="h-4 w-4 text-amber-600" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        )
    } 
    
    // For objects and other complex types
    return (
        <div className="group relative overflow-hidden bg-white/60 backdrop-blur-sm rounded-xl border border-white/50 shadow-sm hover:shadow-md transition-all duration-300">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-50/20 via-transparent to-orange-50/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            <div className="relative p-4">
                <div className="flex justify-between items-start">
                    <pre className="text-xs overflow-auto pr-8 text-gray-800">{JSON.stringify(value, null, 2)}</pre>
                    <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 rounded-full hover:bg-amber-100/50 transition-all duration-300 transform hover:scale-110" 
                        onClick={() => {
                            navigator.clipboard.writeText(JSON.stringify(value, null, 2));
                            toast({
                                title: "Copied!",
                                description: "JSON copied to clipboard",
                            });
                        }}
                    >
                        <Copy className="h-4 w-4 text-amber-600" />
                    </Button>
                </div>
            </div>
        </div>
    )
}

// Export the exit handler function for parent components to use
ContentPreview.displayName = 'ContentPreview'